import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SEOMeta from '@/components/base/SEOMeta';
import JsonLd from '@/components/base/JsonLd';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => category && a.categoryId === category.id);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您访问的分类页面不存在。</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${category.name} - 蜜桃网站${category.description.slice(0, 20)}`}
        description={category.description}
        keywords={`蜜桃网站,${category.name},蜜桃${category.name},蜜桃网站${category.name}频道`}
        canonicalUrl={`https://weifutongmicropay.cn/category/${category.slug}`}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name} - 蜜桃网站`,
          description: category.description,
          url: `https://weifutongmicropay.cn/category/${category.slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '蜜桃网站',
            url: 'https://weifutongmicropay.cn',
          },
          about: {
            '@type': 'Thing',
            name: category.name,
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://weifutongmicropay.cn',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: category.name,
              item: `https://weifutongmicropay.cn/category/${category.slug}`,
            },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumbs
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4 mb-3">
              <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-2xl">
                <i className={category.icon}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
            <p className="text-sm text-foreground-500">
              共 {category.articleCount} 篇文章
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-40 bg-background-50/95 backdrop-blur-sm border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide" aria-label="分类导航">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </nav>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl mx-auto mb-4">
                  <i className="ri-inbox-line"></i>
                </span>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}